import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useProduct } from '../hooks/api-hooks';
import { api } from '../lib/api';
import { ErrorState, Loading } from '../components/States';
import { formatDuration } from '../lib/format';
import type { Lesson, Playback } from '../lib/types';

export function CoursePlayerPage() {
  const { slug } = useParams();
  const product = useProduct(slug);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [playback, setPlayback] = useState<Playback | null>(null);
  const [playbackError, setPlaybackError] = useState<unknown>(null);
  const [loadingPlayback, setLoadingPlayback] = useState(false);

  const courseId = product.data?.course?.id;
  const lessons = useMemo(
    () => product.data?.course?.sections.flatMap((section) => section.lessons) ?? [],
    [product.data?.course?.sections],
  );

  useEffect(() => {
    if (lessons.length === 0) {
      setLesson(null);
      return;
    }
    setLesson((current) => lessons.find((entry) => entry.id === current?.id) ?? lessons[0] ?? null);
  }, [lessons]);

  // A playback token is short lived, so it is requested per lesson opening
  // rather than cached alongside the course outline.
  useEffect(() => {
    if (!courseId || !lesson) return;
    let active = true;
    setLoadingPlayback(true);
    setPlaybackError(null);
    setPlayback(null);

    api
      .lessonPlayback(courseId, lesson.id)
      .then((result) => {
        if (active) setPlayback(result);
      })
      .catch((error: unknown) => {
        if (active) setPlaybackError(error);
      })
      .finally(() => {
        if (active) setLoadingPlayback(false);
      });

    return () => {
      active = false;
    };
  }, [courseId, lesson]);

  if (product.isPending) return <Loading />;
  if (product.error) return <ErrorState error={product.error} />;
  const course = product.data?.course;
  if (!course) return <p className="error">Sản phẩm này không phải khoá học.</p>;
  const currentIndex = lesson ? lessons.findIndex((entry) => entry.id === lesson.id) : -1;
  const previousLesson = currentIndex > 0 ? lessons[currentIndex - 1] : null;
  const nextLesson = currentIndex >= 0 ? lessons[currentIndex + 1] ?? null : null;

  return (
    <div className="player course-player">
      <aside className="player-sidebar" aria-label="Danh sách bài học">
        <h1>{product.data?.title}</h1>
        <p className="muted">{lessons.length} bài học</p>
        {course.sections.map((section) => (
          <section key={section.id} className="player-section">
            <h2>{section.title}</h2>
            <ol className="list player-lessons">
              {section.lessons.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    className="link-button"
                    aria-current={lesson?.id === entry.id ? 'true' : undefined}
                    onClick={() => setLesson(entry)}
                  >
                    {entry.title}
                  </button>
                  <span className="muted">{formatDuration(entry.durationSeconds)}</span>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </aside>

      <main className="player-stage" aria-live="polite">
        {lessons.length === 0 && <p className="notice">Khoá học chưa có bài học.</p>}
        {loadingPlayback && <Loading label="Đang lấy token phát video…" />}
        {playbackError ? <ErrorState error={playbackError} /> : null}
        {playback && lesson && (
          <>
            <p className="player-progress muted">
              Bài {currentIndex + 1} / {lessons.length}
            </p>
            <h2>{lesson.title}</h2>
            <iframe
              title={lesson.title}
              src={playback.iframeUrl}
              allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
              allowFullScreen
              className="video"
            />
            {lesson.description && <p>{lesson.description}</p>}
            <nav className="player-navigation" aria-label="Điều hướng bài học">
              <button
                type="button"
                className="btn btn-quiet"
                disabled={!previousLesson}
                onClick={() => previousLesson && setLesson(previousLesson)}
              >
                Bài trước
              </button>
              <button
                type="button"
                className="btn"
                disabled={!nextLesson}
                onClick={() => nextLesson && setLesson(nextLesson)}
              >
                Bài tiếp theo
              </button>
            </nav>
          </>
        )}
      </main>
    </div>
  );
}
